import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

import type { EmptyProps } from '@change-corgi/core/types';

type ConfirmationCallback = (confirmed: boolean) => void;

type ModalType = 'highAmount' | 'overwritePaymentMethod';

export type ModalConfirmationContextValue = [
	{
		visibleModal: ModalType | null;
	},
	{
		displayModal: (type: ModalType) => void;
		handleModalResult: (result: boolean) => void;
		setModalResultResolveFn: (resolveFn: ConfirmationCallback) => void;
	},
];

export const ModalConfirmationContext = createContext<ModalConfirmationContextValue | null>(null);

export const ModalConfirmationProvider: React.FC<React.PropsWithChildren<EmptyProps>> = ({ children }) => {
	const [visibleModal, setVisibleModal] = useState<ModalType | null>(null);

	const modalResultResolveFn = useRef<ConfirmationCallback | null>(null);

	const displayModal = useCallback(
		(type: ModalType) => {
			setVisibleModal(type);
		},
		[setVisibleModal],
	);

	const handleModalResult = useCallback(
		(result: boolean) => {
			setVisibleModal(null);
			if (modalResultResolveFn.current) {
				modalResultResolveFn.current(result);
				modalResultResolveFn.current = null;
			}
		},
		[modalResultResolveFn],
	);

	const setModalResultResolveFn = useCallback(
		(resolveFn: ConfirmationCallback) => {
			modalResultResolveFn.current = resolveFn;
		},
		[modalResultResolveFn],
	);

	return (
		<ModalConfirmationContext.Provider
			value={[
				{
					visibleModal,
				},
				{
					displayModal,
					handleModalResult,
					setModalResultResolveFn,
				},
			]}
		>
			{children}
		</ModalConfirmationContext.Provider>
	);
};

export function useModalConfirmationContext(): ModalConfirmationContextValue {
	const context = useContext(ModalConfirmationContext);

	if (!context) {
		throw new Error('useModalConfirmationContext must be used within ModalConfirmationProvider');
	}

	return context;
}
